import { useEffect } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Mobile Performance Optimizer Component
 * Applies mobile-specific optimizations when detected
 */
export const MobileOptimizer = () => {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) return;

    // Disable expensive animations on mobile
    document.documentElement.style.setProperty('--animation-duration', '0.1s');
    
    // Add mobile-specific class to body
    document.body.classList.add('mobile-optimized');
    
    // Reduce motion for better performance
    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      document.documentElement.style.setProperty('--motion-reduce', '1');
    }

    // Optimize scrolling
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Add intersection observer for lazy loading images
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const img = entry.target as HTMLImageElement;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
                imageObserver.unobserve(img);
              }
            }
          });
        },
        { rootMargin: '50px' }
      );

      // Observe all images with data-src
      document.querySelectorAll('img[data-src]').forEach((img) => {
        imageObserver.observe(img);
      });
    }

    // Hint browser about content-visibility and performance optimizations
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 767px) {
        /* Lazy loading sections */
        .lazy-section {
          content-visibility: auto;
          contain-intrinsic-size: 600px;
        }
        
        /* Disable expensive effects on mobile */
        .hover-glow,
        .animated-mesh-background,
        .parallax-effect {
          display: none !important;
        }
        
        /* Optimize images */
        img {
          image-rendering: -webkit-optimize-contrast;
        }
        
        /* COMPLETELY DISABLE ALL ANIMATIONS */
        *,
        *::before,
        *::after {
          animation: none !important;
          transition: none !important;
        }
        
        /* Optimize below-fold content */
        section:not(:first-of-type) {
          content-visibility: auto;
        }
        
        /* Prevent layout shifts */
        img:not([width]):not([height]) {
          aspect-ratio: 16/9;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.body.classList.remove('mobile-optimized');
      document.head.removeChild(style);
    };
  }, [isMobile]);

  return null;
};
